import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { InputText } from "primereact/inputtext"
import { Dropdown, DropdownChangeParams } from "primereact/dropdown"
import { Checkbox, CheckboxChangeParams } from "primereact/checkbox"
import { useConstants } from "../../../api/queries/useConstants"
import { ConstantsResponseData } from "../../../api/types/constants"
import { Button } from "primereact/button"
import FieldError, { FormErrors } from "../../FieldError"
import { classNames } from "primereact/utils"
import { PhoneInput } from "../../common/PhoneInput"
import { splitNumber } from "../../common/PhoneInput/utils"
import styles from "../../Order/Form/styles.module.css"
import { useAddressBookDetail } from "../../../api/queries/address-book/useAddressBookDetail"
import { TimeRange } from "../../common/TimeRange"
import { validateEmail, validateIn, validateVatId } from "../../Order/Form/validations/common"
import { useAddressBookUpdate } from "../../../api/queries/address-book/useAddressBookUpdate"
import { useAddressBookCreate } from "../../../api/queries/address-book/useAddressBookCreate"
import { useAddressBookDelete } from "../../../api/queries/address-book/useAddressBookDelete"
import { AddressSimple } from "../../../api/types/address"
import { AddressBookCreatePayload, AddressBookCreateResponseData, AddressBookUpdatePayload, AddressBookUpdateResponseData } from "../../../api/types/address-book"
import { validateAddressBook } from "./validations"
import commonStyles from "../../common.module.css"
import { countryItemTemplate, countryValueTemplate } from "../../common/country"

export default function AddressBookForm() {
  const navigate = useNavigate()
  const { uuid } = useParams<Params>()
  const { data: constants }: { data: ConstantsResponseData | undefined } = useConstants()
  const { data: addressBookResponse } = useAddressBookDetail(uuid)
  const createAddressBook = useAddressBookCreate()
  const updateAddressBook = useAddressBookUpdate(uuid)
  const deleteAddressBook = useAddressBookDelete(uuid)

  const [name, setName] = useState<string>("")
  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
    resetErrorsFor(["name"])
  }

  const [street, setStreet] = useState<string>("")
  const handleStreetChange = (event: ChangeEvent<HTMLInputElement>) => {
    setStreet(event.target.value)
    resetErrorsFor(["street"])
  }

  const [streetDetails, setStreetDetails] = useState<string>("")
  const handleStreetDetailsChange = (event: ChangeEvent<HTMLInputElement>) =>
    setStreetDetails(event.target.value)

  const [zip, setZip] = useState<string>("")
  const handleZIPChange = (event: ChangeEvent<HTMLInputElement>) => {
    setZip(event.target.value)
    resetErrorsFor(["zip"])
  }

  const [city, setCity] = useState<string>("")
  const handleCityChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCity(event.target.value)
    resetErrorsFor(["city"])
  }

  const [country, setCountry] = useState<string>("")
  const handleCountryChange = (event: DropdownChangeParams) => {
    setCountry(event.value)
    resetErrorsFor(["country"])

    const prefix = constants?.results.countries
      .find((country) => country.value === event.value)?.prefix

    if (prefix) {
      setPhonePrefix(prefix)
      setMobilePrefix(prefix)
    }
  }

  const [person, setPerson] = useState<string>("")
  const handlePersonChange = (event: ChangeEvent<HTMLInputElement>) => setPerson(event.target.value)

  const [phonePrefix, setPhonePrefix] = useState<string>("")
  const [phone, setPhone] = useState<number | null>(null)
  const handlePhoneChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setPhonePrefix(prefix)
    setPhone(value)
    resetErrorsFor(["phone", "mobile"])
  }

  const [mobilePrefix, setMobilePrefix] = useState<string>("")
  const [mobile, setMobile] = useState<number | null>(null)
  const handleMobileChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setMobilePrefix(prefix)
    setMobile(value)
    resetErrorsFor(["mobile", "phone"])
  }

  const [email, setEmail] = useState<string>("")
  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)
  const validateEmailBlur = () => setErrors(Object.assign({}, errors, validateEmail(email)))

  const [notifyPhone, setNotifyPhone] = useState<boolean>(false)
  const handleNotifyPhoneChange = (event: CheckboxChangeParams) => setNotifyPhone(event.checked)

  const [notifySMS, setNotifySMS] = useState<boolean>(false)
  const handleNotifySMSChange = (event: CheckboxChangeParams) => setNotifySMS(event.checked)

  const [notifyEmail, setNotifyEmail] = useState<boolean>(false)
  const handleNotifyEmailChange = (event: CheckboxChangeParams) => setNotifyEmail(event.checked)

  const [tailgate, setTailgate] = useState<boolean>(false)
  const handleTailgateChange = (event: CheckboxChangeParams) => setTailgate(event.checked)

  const [loadingTime, setLoadingTime] = useState<string>("08:00-16:00")
  const handleLoadingTimeChange = (value: string) => setLoadingTime(value)

  const [_in, setIn] = useState<string>("")
  const handleInChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setIn(event.target.value)
  const validateInBlur = () => setErrors(Object.assign({}, errors, validateIn(_in)))

  const [vatId, setVatId] = useState<string>("")
  const handleVatIdChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setVatId(event.target.value)
  const validateVatIdBlur = () => setErrors(Object.assign({}, errors, validateVatId(vatId)))

  const [note, setNote] = useState<string>("")
  const onNoteChange = (event: ChangeEvent<HTMLInputElement>) => setNote(event.target.value)

  const [errors, setErrors] = useState<FormErrors<AddressSimple>>()

  const handleFormSubmit = async (event: SyntheticEvent) => {
    event.preventDefault()

    if (uuid) {
      const payload: AddressBookUpdatePayload = {
        name,
        street,
        streetDetails,
        zip,
        city,
        country,
        person,
        phone: phone ? `${phonePrefix}${phone}` : "",
        mobile: mobile ? `${mobilePrefix}${mobile}` : "",
        email,
        notifyPhone,
        notifySMS,
        notifyEmail,
        tailgate,
        loadingTime,
        in: _in || null,
        vatId: vatId || null,
        note,
      }

      const [isValid, errors] = validateAddressBook(payload)
      setErrors(errors)

      if (isValid) {
        await updateAddressBook.mutateAsync(payload)
          .then(() => navigate("/address-book"))
          .catch(({ response }) => {
            let { data }: { data: AddressBookUpdateResponseData } = response
            let respErrors: FormErrors<AddressSimple> = {}

            data.errors.forEach((error) => {
              if (typeof error.property === "string") {
                respErrors[error.property as keyof AddressSimple] = error.message
              }
            })

            setErrors(respErrors)
          })
      }
    } else {
      const payload: AddressBookCreatePayload = {
        name,
        street,
        streetDetails,
        zip,
        city,
        country,
        person,
        phone: phone ? `${phonePrefix}${phone}` : "",
        mobile: mobile ? `${mobilePrefix}${mobile}` : "",
        email,
        notifyPhone,
        notifySMS,
        notifyEmail,
        tailgate,
        loadingTime,
        in: _in || null,
        vatId: vatId || null,
        note,
      }

      const [isValid, errors] = validateAddressBook(payload)
      setErrors(errors)

      if (isValid) {
        await createAddressBook.mutateAsync(payload)
          .then(() => navigate("/address-book"))
          .catch(({ response }) => {
            let { data }: { data: AddressBookCreateResponseData } = response
            let respErrors: FormErrors<AddressSimple> = {}

            data.errors.forEach((error) => {
              if (typeof error.property === "string") {
                respErrors[error.property as keyof AddressSimple] = error.message
              }
            })

            setErrors(respErrors)
          })
      }
    }
  }

  const onDelete = async () => {
    await deleteAddressBook.mutateAsync()
    navigate("/address-book")
  }

  const resetErrorsFor = (names: string[]) => {
    let obj: { [key: string]: string } = {}

    names.forEach((name) => { obj[name] = "" })

    setErrors(Object.assign({}, errors, obj))
  }

  useEffect(() => {
    if (addressBookResponse && constants) {
      const address = addressBookResponse.results.addressBookDetail

      if (address) {
        setName(address.name)
        setStreet(address.street)
        setStreetDetails(address.streetDetails)
        setZip(address.zip)
        setCity(address.city)
        setCountry(address.country)
        setPerson(address.person)
        setEmail(address.email)
        setNotifyPhone(address.notifyPhone)
        setNotifySMS(address.notifySMS)
        setNotifyEmail(address.notifyEmail)
        setTailgate(address.tailgate)
        setIn(address.in || "")
        setVatId(address.vatId || "")
        setNote(address.note)

        if (address.loadingTime) {
          setLoadingTime(address.loadingTime)
        }

        if (address.phone) {
          const { prefix, number } = splitNumber(constants.results.countries, address.phone)
          setPhonePrefix(prefix)
          setPhone(number)
        }

        if (address.mobile) {
          const { prefix, number } = splitNumber(constants.results.countries, address.mobile)
          setMobilePrefix(prefix)
          setMobile(number)
        }
      }
    }
  }, [addressBookResponse, constants])

  return (
    <>
      <form className={"col-12"} onSubmit={handleFormSubmit}>
        <div className={"p-fluid"}>
          <div className={"block-title block-title_blue px-3 py-2 border-round-top flex justify-content-between h-auto align-items-center"}>
            Adresa

            {uuid && (
              <Button type={"button"} label={"Smazat"}
                className={"p-button p-button-sm p-button-danger w-auto p-button-responsive"}
                icon={"pi pi-trash"} onClick={onDelete} />
            )}
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={name} onChange={handleNameChange}
                    className={classNames({ "p-invalid": !!errors?.name })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>Název:</label>
                </span>
                <FieldError error={errors?.name} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={street} onChange={handleStreetChange}
                    className={classNames({ "p-invalid": !!errors?.street })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>Ulice a číslo domu:</label>
                </span>
                <FieldError error={errors?.street} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={streetDetails || ""} onChange={handleStreetDetailsChange}
                    aria-autocomplete={"none"}
                    className={classNames({ "p-invalid": !!errors?.streetDetails })} />
                  <label>Upřesnění:</label>
                </span>
                <FieldError error={errors?.streetDetails} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={zip} onChange={handleZIPChange}
                    className={classNames({ "p-invalid": !!errors?.zip })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>PSČ:</label>
                </span>
                <FieldError error={errors?.zip} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={city} onChange={handleCityChange}
                    className={classNames({ "p-invalid": !!errors?.city })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>Obec:</label>
                </span>
                <FieldError error={errors?.city} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className="p-float-label">
                  <Dropdown options={constants?.results.countries || []}
                    value={country} onChange={handleCountryChange}
                    className={classNames({ "p-invalid": !!errors?.country })}
                    itemTemplate={countryItemTemplate} valueTemplate={countryValueTemplate}
                  />
                  <label className={commonStyles.labelRequired}>Stát:</label>
                </span>
                <FieldError error={errors?.country} />
              </div>
            </div>
          </div>

          <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
            Kontaktní osoba
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-8"}>
                <label className={"mb-3"}>Avizovat:</label>
                <div className={"formgroup-inline"}>
                  <div className={"field-checkbox"}>
                    <Checkbox name={"phone"} inputId={"phone"}
                      checked={notifyPhone} onChange={handleNotifyPhoneChange}
                      className={classNames({ "p-invalid": !!errors?.notifyPhone })} />
                    <label htmlFor={"phone"}>Telefonicky</label>
                  </div>
                  <div className={"field-checkbox"}>
                    <Checkbox name={"sms"} inputId={"sms"}
                      checked={notifySMS} onChange={handleNotifySMSChange}
                      className={classNames({ "p-invalid": !!errors?.notifySMS })} />
                    <label htmlFor={"sms"}>Pomocí SMS</label>
                  </div>
                  <div className={"field-checkbox"}>
                    <Checkbox name={"email"} inputId={"email"}
                      checked={notifyEmail} onChange={handleNotifyEmailChange}
                      className={classNames({ "p-invalid": !!errors?.notifyEmail })} />
                    <label htmlFor={"email"}>E-mailem</label>
                  </div>
                </div>
                <FieldError error={errors?.notifyPhone} />
                <FieldError error={errors?.notifySMS} />
                <FieldError error={errors?.notifyEmail} />
              </div>
              {/*
              <div className={"field col-12 md:col-4"}>
                <div className={"field-checkbox"}>
                  <Checkbox name={"notifyPhone"} inputId={"notifyPhone"}
                    checked={notifyPhone} onChange={handleNotifyPhoneChange}
                    className={classNames({ "p-invalid": !!errors?.notifyPhone })} />
                  <label htmlFor={"notifyPhone"}>Avizovat telefonicky</label>
                </div>
                <FieldError error={errors?.notifyPhone} />
              </div>
                */}

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={person || ""} onChange={handlePersonChange}
                    aria-autocomplete={"none"}
                    className={classNames({ "p-invalid": !!errors?.person })} />
                  <label>Jméno:</label>
                </span>
                <FieldError error={errors?.person} />
              </div>
            </div>

            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <PhoneInput placeholder={"Mobil:"} prefix={mobilePrefix} value={mobile} onChange={handleMobileChange}
                  isRequired={(notifyPhone && !phone) || notifySMS} error={errors?.mobile} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <PhoneInput placeholder={"Telefon:"} prefix={phonePrefix} value={phone} onChange={handlePhoneChange}
                  isRequired={notifyPhone && !mobile} error={errors?.phone} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={email || ""} onChange={handleEmailChange} onBlur={validateEmailBlur}
                    className={classNames({ "p-invalid": !!errors?.email })}
                    aria-autocomplete={"none"} />
                  <label className={notifyEmail ? commonStyles.labelRequired : ""}>E-mail:</label>
                </span>
                <FieldError error={errors?.email} />
              </div>
            </div>
          </div>

          <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
            Důležité informace
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <div className={"field-checkbox"}>
                  <Checkbox name={"tailgate"} inputId={"tailgate"}
                    checked={tailgate} onChange={handleTailgateChange}
                    className={classNames({ "p-invalid": !!errors?.tailgate })} />
                  <label htmlFor={"tailgate"}>Auto s čelem</label>
                </div>
                <FieldError error={errors?.tailgate} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <TimeRange onChange={handleLoadingTimeChange} value={loadingTime}
                    error={!!errors?.loadingTime} />
                  <label>Pracovní doba:</label>
                </span>
                <FieldError error={errors?.loadingTime} />
              </div>

              <div className={"field col-12"}>
                <span className={"p-float-label"}>
                  <InputText maxLength={50} value={note || ""} onChange={onNoteChange}
                    className={classNames({ "p-invalid": !!errors?.note })} />
                  <label>Dispozice:</label>
                </span>
                <FieldError error={errors?.note} />
              </div>
            </div>
          </div>

          <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
            Podmínky přepravy
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={_in || ""} onChange={handleInChange} onBlur={validateInBlur}
                    className={classNames({ "p-invalid": !!errors?.in })}
                  />
                  <label>IČ:</label>
                </span>
                <FieldError error={errors?.in} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={vatId || ""} onChange={handleVatIdChange} onBlur={validateVatIdBlur}
                    className={classNames({ "p-invalid": !!errors?.vatId })}
                  />
                  <label>DIČ:</label>
                </span>
                <FieldError error={errors?.vatId} />
              </div>
            </div>
          </div>
        </div>

        <div className={`${styles.controls} md:flex`}>
          <span className={"flex-1 text-right"}>
            <Button type={"submit"} label={"Uložit"}
              className={"p-button p-button-sm p-button-success"}
              disabled={updateAddressBook.isLoading}
              icon={"pi pi-save"} />
          </span>
        </div>
      </form>
    </>
  )
}

export type Params = "uuid" 
